'use strict';

const VIEWPORT_MARGIN = 8;
const MIN_MENU_HEIGHT = 120;
const DEFAULT_PREFERRED_MAX_HEIGHT = 300;
const SELECT2_PREFERRED_MAX_HEIGHT = 280;
const SELECT2_MIN_RESULTS_HEIGHT = 100;

const menuPortalHome = new WeakMap();

export function computeViewportPlacement(anchorRect, preferredMaxHeight, minHeight) {
    const preferred = preferredMaxHeight > 0 ? preferredMaxHeight : DEFAULT_PREFERRED_MAX_HEIGHT;
    const min = minHeight > 0 ? minHeight : MIN_MENU_HEIGHT;
    const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = anchorRect.top - VIEWPORT_MARGIN;
    const fitsBelow = spaceBelow >= preferred;
    const placeAbove = !fitsBelow && spaceAbove > spaceBelow && spaceAbove >= min;
    const available = placeAbove ? spaceAbove : spaceBelow;
    return {
        placement: placeAbove ? 'above' : 'below',
        maxHeight: Math.max(min, Math.min(preferred, available)),
    };
}

function resolvePortalDestination(menuEl) {
    const modal = menuEl.closest('.modal');
    return modal || document.body;
}

function portalMenu(menuEl) {
    if (!menuEl) {
        return;
    }
    const destination = resolvePortalDestination(menuEl);
    if (menuEl.parentElement === destination) {
        return;
    }
    menuPortalHome.set(menuEl, {
        parent: menuEl.parentElement,
        nextSibling: menuEl.nextSibling,
    });
    destination.appendChild(menuEl);
}

function restoreMenu(menuEl) {
    if (!menuEl) {
        return;
    }
    const home = menuPortalHome.get(menuEl);
    if (!home || !home.parent) {
        return;
    }
    if (home.nextSibling && home.nextSibling.parentNode === home.parent) {
        home.parent.insertBefore(menuEl, home.nextSibling);
    } else {
        home.parent.appendChild(menuEl);
    }
    menuPortalHome.delete(menuEl);
}

export function positionFixedDropdownMenu(menuEl, triggerEl, options) {
    if (!menuEl || !triggerEl) {
        throw new Error('positionFixedDropdownMenu: menu and trigger are required');
    }

    if (!menuEl.classList.contains('show')) {
        return;
    }

    const preferredMaxHeight = options && options.preferredMaxHeight
        ? options.preferredMaxHeight
        : DEFAULT_PREFERRED_MAX_HEIGHT;
    const minHeight = options && options.minHeight ? options.minHeight : MIN_MENU_HEIGHT;
    const zIndex = options && options.zIndex ? options.zIndex : 2000;
    const anchor = triggerEl.getBoundingClientRect();
    const placementResult = computeViewportPlacement(anchor, preferredMaxHeight, minHeight);
    const placement = placementResult.placement;
    const maxHeight = placementResult.maxHeight;

    portalMenu(menuEl);

    menuEl.classList.add('dropdown-menu--viewport-fixed');
    menuEl.classList.toggle('dropdown-menu--above', placement === 'above');
    menuEl.style.setProperty('position', 'fixed', 'important');
    menuEl.style.setProperty('left', anchor.left + 'px', 'important');
    menuEl.style.setProperty('width', Math.max(anchor.width, 180) + 'px', 'important');
    menuEl.style.setProperty('right', 'auto', 'important');
    menuEl.style.setProperty('display', 'flex', 'important');
    menuEl.style.setProperty('flex-direction', 'column', 'important');
    menuEl.style.setProperty('height', maxHeight + 'px', 'important');
    menuEl.style.setProperty('max-height', maxHeight + 'px', 'important');
    menuEl.style.setProperty('box-sizing', 'border-box', 'important');
    menuEl.style.setProperty('overflow-x', 'hidden', 'important');
    menuEl.style.setProperty('z-index', String(zIndex), 'important');
    menuEl.style.setProperty('transform', 'none', 'important');
    menuEl.style.setProperty('margin', '0', 'important');
    menuEl.style.setProperty('float', 'none', 'important');

    const optionsEl = menuEl.querySelector('.multi-select-dropdown-options');
    if (optionsEl) {
        optionsEl.style.removeProperty('max-height');
        optionsEl.style.overflowY = 'auto';
        menuEl.style.setProperty('overflow-y', 'hidden', 'important');
    } else {
        menuEl.style.setProperty('overflow-y', 'auto', 'important');
    }

    if (placement === 'above') {
        menuEl.style.setProperty('top', 'auto', 'important');
        menuEl.style.setProperty(
            'bottom',
            (window.innerHeight - anchor.top + VIEWPORT_MARGIN) + 'px',
            'important'
        );
    } else {
        menuEl.style.setProperty('top', (anchor.bottom + VIEWPORT_MARGIN) + 'px', 'important');
        menuEl.style.setProperty('bottom', 'auto', 'important');
    }
}

export function clearFixedDropdownMenu(menuEl) {
    if (!menuEl) {
        return;
    }
    menuEl.classList.remove('dropdown-menu--viewport-fixed', 'dropdown-menu--above');
    [
        'position',
        'left',
        'width',
        'right',
        'top',
        'bottom',
        'height',
        'max-height',
        'overflow-y',
        'overflow-x',
        'z-index',
        'transform',
        'margin',
        'float',
        'display',
        'flex-direction',
        'box-sizing',
    ].forEach(function (prop) {
        menuEl.style.removeProperty(prop);
    });
    restoreMenu(menuEl);
    const optionsEl = menuEl.querySelector('.multi-select-dropdown-options');
    if (optionsEl) {
        optionsEl.style.removeProperty('max-height');
        optionsEl.style.removeProperty('overflow-y');
    }
}

export function fitSelect2DropdownToViewport(select) {
    const $select = $(select);
    if (!$select.length) {
        return;
    }
    const data = $select.data('select2');
    if (!data || !data.$dropdown || !data.$selection) {
        return;
    }

    const selectionEl = data.$selection[0];
    if (!selectionEl) {
        return;
    }

    const $dropdown = data.$dropdown;
    const $results = $dropdown.find('.select2-results__options');
    if (!$results.length) {
        return;
    }

    const anchor = selectionEl.getBoundingClientRect();
    const searchHeight = $dropdown.find('.select2-search:visible').outerHeight(true) || 0;
    const chrome = searchHeight + 24;
    const spaceBelow = window.innerHeight - anchor.bottom - VIEWPORT_MARGIN - chrome;
    const spaceAbove = anchor.top - VIEWPORT_MARGIN - chrome;
    const openAbove = $dropdown.hasClass('select2-dropdown--above')
        || (spaceBelow < SELECT2_PREFERRED_MAX_HEIGHT && spaceAbove > spaceBelow);
    const available = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(
        SELECT2_MIN_RESULTS_HEIGHT,
        Math.min(SELECT2_PREFERRED_MAX_HEIGHT, available)
    );

    $results.css('max-height', maxHeight + 'px');

    if (data.dropdown && typeof data.dropdown._positionDropdown === 'function') {
        data.dropdown._positionDropdown();
    }
    if (data.dropdown && typeof data.dropdown._resizeDropdown === 'function') {
        data.dropdown._resizeDropdown();
    }
}

function resolveDropdownMenu(triggerEl) {
    if (!triggerEl) {
        return null;
    }
    const cached = triggerEl._viewportDropdownMenu;
    if (cached && cached.isConnected) {
        return cached;
    }
    if (triggerEl.id) {
        const labelled = document.querySelector(
            '.dropdown-menu[aria-labelledby="' + CSS.escape(triggerEl.id) + '"]'
        );
        if (labelled) {
            triggerEl._viewportDropdownMenu = labelled;
            return labelled;
        }
    }
    const $trigger = $(triggerEl);
    const $sibling = $trigger.siblings('.dropdown-menu').first();
    if ($sibling.length) {
        triggerEl._viewportDropdownMenu = $sibling[0];
        return $sibling[0];
    }
    const $root = $trigger.closest('.dropdown, .multi-select-dropdown, .btn-group');
    const $child = $root.children('.dropdown-menu').first();
    if ($child.length) {
        triggerEl._viewportDropdownMenu = $child[0];
        return $child[0];
    }
    return null;
}

export function bindViewportAwareDropdown($trigger, options) {
    if (!$trigger || !$trigger.length) {
        throw new Error('bindViewportAwareDropdown: trigger is required');
    }
    if (typeof $trigger.dropdown !== 'function') {
        throw new Error('bindViewportAwareDropdown: Bootstrap dropdown is required');
    }

    const triggerEl = $trigger[0];
    const $parent = $trigger.closest('.dropdown, .multi-select-dropdown, .btn-group');
    if (!$parent.length) {
        throw new Error('bindViewportAwareDropdown: dropdown parent is required');
    }

    const opts = options || {};
    const ns = opts.namespace || 'viewportDropdown';
    const positionOpts = {
        preferredMaxHeight: opts.preferredMaxHeight,
        minHeight: opts.minHeight,
        zIndex: opts.zIndex,
    };

    try {
        $trigger.dropdown('dispose');
    } catch {
    }

    $trigger.dropdown({ display: 'static' });

    let positionToken = 0;

    function isThisTrigger(event) {
        const related = event.relatedTarget;
        return !related || related === triggerEl;
    }

    function reposition() {
        const menuEl = resolveDropdownMenu(triggerEl);
        if (!menuEl || !menuEl.classList.contains('show')) {
            return;
        }
        positionFixedDropdownMenu(menuEl, triggerEl, positionOpts);
    }

    function stopPositioning() {
        positionToken += 1;
        window.removeEventListener('resize', reposition);
        window.removeEventListener('scroll', reposition, true);
        clearFixedDropdownMenu(resolveDropdownMenu(triggerEl));
    }

    $parent
        .off('.' + ns)
        .on('show.bs.dropdown.' + ns, function (event) {
            if (!isThisTrigger(event)) {
                return;
            }
            const menuEl = resolveDropdownMenu(triggerEl);
            if (!menuEl) {
                return;
            }
            portalMenu(menuEl);
        })
        .on('shown.bs.dropdown.' + ns, function (event) {
            if (!isThisTrigger(event)) {
                return;
            }
            const menuEl = resolveDropdownMenu(triggerEl);
            if (!menuEl) {
                return;
            }
            positionFixedDropdownMenu(menuEl, triggerEl, positionOpts);
            const token = positionToken;
            requestAnimationFrame(function () {
                if (token !== positionToken) {
                    return;
                }
                positionFixedDropdownMenu(menuEl, triggerEl, positionOpts);
            });
            window.addEventListener('resize', reposition);
            window.addEventListener('scroll', reposition, true);
        })
        .on('hide.bs.dropdown.' + ns, function (event) {
            if (!isThisTrigger(event)) {
                return;
            }
            stopPositioning();
        });

    const menuEl = resolveDropdownMenu(triggerEl);
    if (menuEl) {
        $(menuEl)
            .off('click.' + ns)
            .on('click.' + ns, function (event) {
                event.stopPropagation();
            });
    }
}
